import React, { useEffect, useState } from 'react';
import distributorService from '../services/distributorService';
import { captureSession, isSessionCurrent } from '../utils/authSession';

// Controlled selectors follow Users; organization names are advisory, never a client-side scope.
export default function AdminBusinessContextFilter({
  businessContext,
  distributorId,
  onChange,
  hasDistributorRecords = false,
  onDistributors,
  t,
}) {
  const [distributors, setDistributors] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const needed = businessContext === 'distributor' || hasDistributorRecords;
  useEffect(() => {
    if (!needed) return;
    let active = true;
    const session = captureSession();
    setLoading(true);
    Promise.resolve()
      .then(() =>
        active && isSessionCurrent(session) ? distributorService.adminDistributors() : undefined
      )
      .then((response) => {
        if (!active || !isSessionCurrent(session)) return;
        const rows = response?.data?.data;
        if (!Array.isArray(rows) || rows.some((row) => !row._id || typeof row.name !== 'string'))
          throw new Error('Invalid distributors');
        setDistributors(rows);
        onDistributors?.(rows);
        setError(false);
      })
      .catch(() => {
        if (!active || !isSessionCurrent(session)) return;
        setDistributors([]);
        onDistributors?.([]);
        setError(true);
      })
      .finally(() => {
        if (active && isSessionCurrent(session)) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [needed, reload, onDistributors]);
  const inputClass = 'h-[42px] px-3 border border-[#d6dce8] rounded-[8px] text-sm max-w-full';
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <select
        aria-label={t('Filter by portal')}
        value={businessContext}
        className={inputClass}
        onChange={(event) => onChange(event.target.value, '')}
      >
        <option value="all">{t('All portals')}</option>
        <option value="direct">{t('Direct MedEffects')}</option>
        <option value="distributor">{t('Distributor')}</option>
      </select>
      {businessContext === 'distributor' && (
        <select
          aria-label={t('Filter by distributor')}
          value={distributorId}
          className={inputClass}
          disabled={loading || error || !distributors.length}
          onChange={(event) => onChange(businessContext, event.target.value)}
        >
          <option value="">{t('All distributors')}</option>
          {distributorId && !distributors.some((row) => row._id === distributorId) && (
            <option value={distributorId}>{t('Selected distributor')}</option>
          )}
          {distributors.map((row) => (
            <option key={row._id} value={row._id}>
              {row.name}
            </option>
          ))}
        </select>
      )}
      {error && businessContext === 'distributor' && (
        <p role="alert" className="text-sm text-red-700">
          {t('Distributor names are unavailable. The list still uses the selected portal filter.')}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => setReload((value) => value + 1)}
          >
            {t('Retry')}
          </button>
        </p>
      )}
    </div>
  );
}
